import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Table,
  Form,
  Input,
  Select,
  InputNumber,
  Typography,
  App,
  Space,
  Alert,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import {
  goodsReceiptNoteService,
  type CreateGRNDTO,
  type GRNLineDTO,
  type ReceivablePOLineDTO,
  getGrnLineHeatLines,
} from '@/services/goodsReceiptNote.service';
import { useWarehouses } from '@/hooks/useInventory';
import inventoryService, { type Item } from '@/services/inventory.service';
import { inventoryUomFromSelectOption, ItemSelect } from '@/components/inventory/ItemSelect';
import { useUsers } from '@/hooks/useUsers';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import DocumentUpload from '@/components/documents/DocumentUpload';
import { headerProjectLabel, projectLabel } from '@/utils/projectDisplay';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';
import {
  GrnActualWeightInput,
  expectedReceiptWeight,
  lineTheoreticalWeight,
} from '@/components/procurement/GrnWeightVariance';
import { DOCUMENT_ENTITY_TYPE_GOODS_RECEIPT_NOTE } from '@/constants/document-entity-types';
import { formatUserRecord } from '@/utils/userDisplay';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { grnLineNeedsHeat, grnLineNeedsMtc, grnTraceItemCode } from '@/utils/grnCriticalReceipt';

const { Title, Text } = Typography;

type HeatLineState = {
  id?: string;
  heat_number: string;
  dimension: string;
  quantity: number;
  mtc_document_id?: string | null;
};

function stockIdForEditLine(
  line: GRNLineDTO,
  meta: ReceivablePOLineDTO | undefined,
  stockItemByLine: Record<string, string | null>
): string | null {
  if (meta?.item_type !== 'Non-Stock') return null;
  if (Object.prototype.hasOwnProperty.call(stockItemByLine, line.po_item_id)) {
    return stockItemByLine[line.po_item_id] || null;
  }
  return line.stock_item_id || meta.default_stock_item_id || null;
}

const EditGRNPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [form] = Form.useForm();
  const [lineQuantities, setLineQuantities] = useState<Record<string, { received: number; accepted: number; rejected: number; hold: number }>>({});
  const [heatLinesByPoItem, setHeatLinesByPoItem] = useState<Record<string, HeatLineState[]>>({});
  const [stockItemByLine, setStockItemByLine] = useState<Record<string, string | null>>({});
  const [linkedUomByLine, setLinkedUomByLine] = useState<Record<string, string>>({});
  const [actualWeightByLine, setActualWeightByLine] = useState<Record<string, number | null>>({});
  const [submitLoading, setSubmitLoading] = useState(false);

  const { data: grn, isLoading } = useQuery({
    queryKey: ['goods-receipt-note', id],
    queryFn: () => goodsReceiptNoteService.getById(id!),
    enabled: !!id,
  });

  const { data: receivableLines = [] } = useQuery({
    queryKey: ['grn-receivable-lines', grn?.po_id],
    queryFn: () => goodsReceiptNoteService.getReceivableLines(grn!.po_id!),
    enabled: !!grn?.po_id,
  });

  const poMeta = useMemo(() => new Map(receivableLines.map((l) => [l.id, l])), [receivableLines]);

  const linkedStockIds = useMemo(() => {
    const ids = new Set<string>();
    for (const line of grn?.items ?? []) {
      const id = stockIdForEditLine(line, poMeta.get(line.po_item_id), stockItemByLine);
      if (id) ids.add(id);
    }
    return [...ids];
  }, [grn?.items, poMeta, stockItemByLine]);

  const linkedItemQueries = useQueries({
    queries: linkedStockIds.map((id) => ({
      queryKey: ['items', id],
      queryFn: () => inventoryService.getItemById(id),
      staleTime: 60_000,
    })),
  });

  const linkedById = useMemo(() => {
    const map = new Map<string, Item>();
    linkedStockIds.forEach((id, index) => {
      const item = linkedItemQueries[index]?.data;
      if (item) map.set(id, item);
    });
    return map;
  }, [linkedStockIds, linkedItemQueries]);

  const linkedItemFor = (line: GRNLineDTO) => {
    const id = stockIdForEditLine(line, poMeta.get(line.po_item_id), stockItemByLine);
    return id ? linkedById.get(id) : undefined;
  };

  const { data: warehouses = [] } = useWarehouses();
  const { data: users = [] } = useUsers({
    is_active: true,
    include_user_id: grn?.received_by || user?.id,
  });
  const { isCriticalCode } = useCriticalItemLookup();
  const warehouseOptions = warehouses.map((w) => ({ value: w.id, label: w.warehouse_name || w.warehouse_code }));
  const userOptions = users.map((u) => ({
    value: u.id,
    label: formatUserRecord(u) || u.username || u.id,
  }));

  useEffect(() => {
    if (grn) {
      form.setFieldsValue({
        grn_date: grn.grn_date?.slice(0, 10),
        received_by: grn.received_by,
        warehouse_id: grn.warehouse_id,
        warehouse_location: grn.warehouse_location,
        supplier_delivery_note: grn.supplier_delivery_note,
        vehicle_number: grn.vehicle_number,
        notes: grn.notes,
        department_id: grn.department_id ?? undefined,
      });
      const q: Record<string, { received: number; accepted: number; rejected: number; hold: number }> = {};
      (grn.items ?? []).forEach((line) => {
        q[line.po_item_id] = {
          received: Number(line.received_quantity),
          accepted: Number(line.accepted_quantity),
          rejected: Number(line.rejected_quantity ?? 0),
          hold: Number(line.hold_quantity ?? 0),
        };
      });
      setLineQuantities(q);
      const linked: Record<string, string | null> = {};
      (grn.items ?? []).forEach((line) => {
        linked[line.po_item_id] = line.stock_item_id ?? null;
      });
      setStockItemByLine(linked);
      const weights: Record<string, number | null> = {};
      (grn.items ?? []).forEach((line) => {
        weights[line.po_item_id] = line.actual_weight != null && line.actual_weight !== '' ? Number(line.actual_weight) : null;
      });
      setActualWeightByLine(weights);

      const heat: Record<string, HeatLineState[]> = {};
      (grn.items ?? []).forEach((line) => {
        const hl = getGrnLineHeatLines(line);
        if (hl.length > 0) {
          heat[line.po_item_id] = hl.map((x) => ({
            id: x.id,
            heat_number: x.heat_number,
            dimension: String(x.dimension ?? '').trim(),
            quantity: Number(x.quantity),
            mtc_document_id: x.mtc_document_id ?? null,
          }));
        }
      });
      setHeatLinesByPoItem(heat);
    }
  }, [grn, form]);

  useEffect(() => {
    if (!grn?.items?.length || !receivableLines.length) return;
    setHeatLinesByPoItem((prev) => {
      const next = { ...prev };
      for (const line of grn.items || []) {
        const meta = poMeta.get(line.po_item_id);
        const linkedId = stockIdForEditLine(line, meta, stockItemByLine);
        if (!grnLineNeedsHeat(meta, linkedId ? linkedById.get(linkedId) : undefined)) continue;
        if (next[line.po_item_id]?.length) continue;
        const acc = Number(line.accepted_quantity);
        next[line.po_item_id] = [{ heat_number: '', dimension: '', quantity: acc }];
      }
      return next;
    });
  }, [grn?.items, grn?.id, receivableLines.length, poMeta, stockItemByLine, linkedById]);

  const updateHeatLine = (poItemId: string, index: number, patch: Partial<HeatLineState>) => {
    setHeatLinesByPoItem((prev) => {
      const rows = [...(prev[poItemId] || [])];
      if (!rows[index]) return prev;
      rows[index] = { ...rows[index], ...patch };
      return { ...prev, [poItemId]: rows };
    });
  };

  const addHeatLine = (poItemId: string) => {
    setHeatLinesByPoItem((prev) => ({
      ...prev,
      [poItemId]: [...(prev[poItemId] || []), { heat_number: '', dimension: '', quantity: 0 }],
    }));
  };

  const removeHeatLine = (poItemId: string, index: number) => {
    setHeatLinesByPoItem((prev) => {
      const rows = [...(prev[poItemId] || [])];
      rows.splice(index, 1);
      return { ...prev, [poItemId]: rows };
    });
  };

  const setQuantity = (poItemId: string, field: 'received' | 'accepted' | 'rejected' | 'hold', value: number) => {
    setLineQuantities((prev) => {
      const current = prev[poItemId] ?? { received: 0, accepted: 0, rejected: 0, hold: 0 };
      const next = { ...current, [field]: value };
      if (field === 'received') {
        next.accepted = Math.min(next.accepted, value);
        next.rejected = Math.min(next.rejected, value);
        next.hold = Math.min(next.hold, value);
        if (next.accepted + next.rejected + next.hold !== value) {
          next.accepted = value;
          next.rejected = 0;
          next.hold = 0;
        }
      }
      return { ...prev, [poItemId]: next };
    });
  };

  const handleSubmit = async () => {
    if (!grn || grn.grn_status !== 'Draft') {
      message.warning('Only Draft GRN can be edited');
      return;
    }
    const grnDate = form.getFieldValue('grn_date');
    const receivedBy = form.getFieldValue('received_by');
    const warehouseId = form.getFieldValue('warehouse_id');
    if (!grnDate || !receivedBy) {
      message.warning('GRN date and Received by are required');
      return;
    }
    if (!warehouseId) {
      message.warning('Warehouse is required');
      return;
    }
    if (!form.getFieldValue('department_id')) {
      message.warning('Department is required');
      return;
    }
    const items = grn.items ?? [];
    const lines = items.map((line) => {
      const q = lineQuantities[line.po_item_id] ?? {
        received: Number(line.received_quantity),
        accepted: Number(line.accepted_quantity),
        rejected: Number(line.rejected_quantity ?? 0),
        hold: Number(line.hold_quantity ?? 0),
      };
      const meta = poMeta.get(line.po_item_id);
      const linked = linkedItemFor(line);
      const hlState = heatLinesByPoItem[line.po_item_id];
      const heat_lines =
        hlState && hlState.length > 0
          ? hlState.map((h) => ({
              heat_number: String(h.heat_number ?? '').trim(),
              dimension: String(h.dimension ?? '').trim(),
              quantity: Number(h.quantity) || 0,
              mtc_document_id: h.mtc_document_id?.trim() || null,
            }))
          : grnLineNeedsHeat(meta, linked)
            ? [{ heat_number: '', dimension: '', quantity: q.accepted }]
            : undefined;
      return {
        po_item_id: line.po_item_id,
        received_quantity: q.received,
        accepted_quantity: q.accepted,
        rejected_quantity: q.rejected,
        hold_quantity: q.hold,
        stock_item_id:
          meta?.item_type === 'Non-Stock'
            ? Object.prototype.hasOwnProperty.call(stockItemByLine, line.po_item_id)
              ? stockItemByLine[line.po_item_id] || null
              : line.stock_item_id || meta.default_stock_item_id || null
            : null,
        heat_lines,
        ...(actualWeightByLine[line.po_item_id] != null
          ? { actual_weight: actualWeightByLine[line.po_item_id], actual_weight_uom: line.actual_weight_uom || 'KG' }
          : {}),
      };
    });
    const invalid = lines.find((l) => l.received_quantity <= 0 || l.accepted_quantity + l.rejected_quantity + l.hold_quantity !== l.received_quantity);
    if (invalid) {
      message.warning('Each line must have received > 0 and accepted + rejected + hold = received');
      return;
    }
    for (const line of items) {
      const meta = poMeta.get(line.po_item_id);
      const linked = linkedItemFor(line);
      const traceCode = grnTraceItemCode(meta, linked) || String(line.item_code ?? line.line_number);
      if (!grnLineNeedsHeat(meta, linked)) continue;
      const q = lineQuantities[line.po_item_id] ?? {
        received: 0,
        accepted: 0,
        rejected: 0,
        hold: 0,
      };
      const hl = heatLinesByPoItem[line.po_item_id] || [];
      if (hl.length === 0) {
        message.warning(`Add heat lines for critical item ${traceCode}`);
        return;
      }
      const sum = hl.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
      if (Math.abs(sum - q.accepted) > 0.0001) {
        message.warning(`Heat quantities must sum to accepted (${q.accepted}) for ${traceCode}`);
        return;
      }
      if (hl.some((r) => !String(r.heat_number ?? '').trim())) {
        message.warning(`Enter heat number for each line on ${traceCode}`);
        return;
      }
      if (hl.some((r) => !String(r.dimension ?? '').trim())) {
        message.warning(`Enter dimension for each line on ${traceCode}`);
        return;
      }
      if (grnLineNeedsMtc(meta, linked) && hl.some((r) => !String(r.mtc_document_id ?? '').trim())) {
        message.warning(`MTC document is required for each heat on item ${traceCode}`);
        return;
      }
    }
    const dto: Partial<CreateGRNDTO> & { lines: CreateGRNDTO['lines'] } = {
      grn_date: grnDate,
      receipt_time: form.getFieldValue('receipt_time'),
      supplier_delivery_note: form.getFieldValue('supplier_delivery_note'),
      vehicle_number: form.getFieldValue('vehicle_number'),
      warehouse_id: form.getFieldValue('warehouse_id'),
      warehouse_location: form.getFieldValue('warehouse_location'),
      received_by: receivedBy,
      notes: form.getFieldValue('notes'),
      department_id: form.getFieldValue('department_id'),
      lines,
    };
    setSubmitLoading(true);
    try {
      await goodsReceiptNoteService.update(id!, dto);
      message.success('GRN updated');
      queryClient.invalidateQueries({ queryKey: ['goods-receipt-note', id] });
      queryClient.invalidateQueries({ queryKey: ['goods-receipt-notes'] });
      navigate(`/scm/procurement/grn/${id}`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to update GRN');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!id) return null;
  if (isLoading && !grn) return <Card loading />;
  if (!grn) return <Card><p>GRN not found.</p></Card>;
  if (grn.grn_status !== 'Draft') {
    message.warning('Only Draft GRN can be edited');
    navigate(`/scm/procurement/grn/${id}`);
    return null;
  }

  const items = grn.items ?? [];

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/scm/procurement/grn/${id}`)} style={{ marginBottom: 16 }}>
        Back to GRN
      </Button>
      <Title level={4} style={{ marginBottom: 24 }}>
        Edit GRN {grn.grn_number}
      </Title>

      {(Boolean(grn.inspection_required) ||
        items.some((line) => Boolean(poMeta.get(line.po_item_id)?.is_critical) || Boolean(linkedItemFor(line)?.is_critical))) && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="Critical items require QC before posting"
          description="After approval, quality must Pass inspection before stock/GL post or supplier invoice."
        />
      )}

      <Form form={form} layout="vertical">
        <Card style={{ marginBottom: 16 }}>
          <Form.Item name="grn_date" label="GRN Date" rules={[{ required: true }]}>
            <Input type="date" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="received_by" label="Received by" rules={[{ required: true, message: 'Received by is required' }]}>
            <Select
              placeholder="Select user"
              style={{ width: '100%', maxWidth: 360 }}
              options={userOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="warehouse_id" label="Warehouse" rules={[{ required: true, message: 'Warehouse is required' }]}>
            <Select
              placeholder="Select warehouse"
              style={{ width: 300 }}
              options={warehouseOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="warehouse_location" label="Warehouse location">
            <Input style={{ maxWidth: 300 }} />
          </Form.Item>
          <Form.Item name="supplier_delivery_note" label="Supplier delivery note">
            <Input style={{ maxWidth: 300 }} />
          </Form.Item>
          <Form.Item name="vehicle_number" label="Vehicle number">
            <Input style={{ maxWidth: 200 }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} style={{ maxWidth: 400 }} />
          </Form.Item>
          <Form.Item label="Project">
            <Input readOnly value={headerProjectLabel(grn, grn.items)} />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect style={{ maxWidth: 400 }} />
          </Form.Item>
        </Card>

        <Card title="Lines" style={{ marginBottom: 16 }}>
          <Table
            size="small"
            rowKey="po_item_id"
            pagination={false}
            dataSource={items}
            rowClassName={(r: GRNLineDTO) =>
              getCriticalItemRowClassName({
                is_critical:
                  Boolean(poMeta.get(r.po_item_id)?.is_critical) ||
                  Boolean(linkedItemFor(r)?.is_critical) ||
                  isCriticalCode(r.item_code),
              })
            }
            columns={[
              { title: '#', dataIndex: 'line_number', width: 50 },
              {
                title: 'Item',
                dataIndex: 'item_code',
                width: 140,
                render: (code: string, r: GRNLineDTO) => {
                  const critical =
                    Boolean(poMeta.get(r.po_item_id)?.is_critical) ||
                    Boolean(linkedItemFor(r)?.is_critical) ||
                    isCriticalCode(r.item_code);
                  return (
                    <Space size={6} wrap>
                      <span>{code}</span>
                      <CriticalItemTag critical={isItemCritical({ is_critical: critical })} />
                    </Space>
                  );
                },
              },
              {
                title: 'Description',
                dataIndex: 'item_description',
                render: (desc: string, r: GRNLineDTO) => {
                  const meta = poMeta.get(r.po_item_id);
                  return (
                    <>
                      <div>{desc}</div>
                      <LineSpecificationsText value={r.specifications} />
                      {meta?.stock_quantity != null &&
                      !(Object.prototype.hasOwnProperty.call(stockItemByLine, r.po_item_id)
                        ? stockItemByLine[r.po_item_id]
                        : r.stock_item_id ?? meta.default_stock_item_id) ? (
                        <div style={{ fontSize: 12, color: '#888' }}>
                          Stock in: {Number(meta.stock_quantity)} {meta.stock_uom || ''}
                        </div>
                      ) : null}
                    </>
                  );
                },
              },
              {
                title: 'Supplier Description',
                dataIndex: 'supplier_description',
                render: (v: string, r: GRNLineDTO) => v || r.item_description || '—',
              },
              {
                title: 'Inventory item',
                width: 220,
                render: (_: unknown, r: GRNLineDTO) => {
                  const meta = poMeta.get(r.po_item_id);
                  if (meta?.item_type !== 'Non-Stock') return '—';
                  return (
                    <ItemSelect
                      allowClear
                      allowedItemTypes={['Stock', 'Consumable']}
                      placeholder="Link a stock item"
                      value={
                        Object.prototype.hasOwnProperty.call(stockItemByLine, r.po_item_id)
                          ? stockItemByLine[r.po_item_id] ?? undefined
                          : r.stock_item_id ?? meta.default_stock_item_id ?? undefined
                      }
                      onChange={(v, option) => {
                        setStockItemByLine((prev) => ({
                          ...prev,
                          [r.po_item_id]: v ? String(v) : null,
                        }));
                        const uom = v ? inventoryUomFromSelectOption(option) : null;
                        setLinkedUomByLine((prev) => {
                          const next = { ...prev };
                          if (uom) next[r.po_item_id] = uom;
                          else delete next[r.po_item_id];
                          return next;
                        });
                      }}
                    />
                  );
                },
              },
              {
                title: 'Project',
                key: 'project',
                width: 120,
                ellipsis: true,
                render: (_: unknown, r: GRNLineDTO) => {
                  const meta = poMeta.get(r.po_item_id);
                  return projectLabel(meta ?? { project_id: r.project_id });
                },
              },
              { title: 'Ordered', dataIndex: 'ordered_quantity', width: 80, align: 'right', render: (v: number) => Number(v).toLocaleString() },
              {
                title: 'UOM',
                dataIndex: 'uom',
                width: 70,
                render: (uom: string, r: GRNLineDTO) => linkedUomByLine[r.po_item_id] || uom,
              },
              {
                title: 'Received qty',
                key: 'received',
                width: 110,
                render: (_: unknown, r: GRNLineDTO) => (
                  <InputNumber
                    min={0}
                    value={lineQuantities[r.po_item_id]?.received ?? Number(r.received_quantity)}
                    onChange={(v) => setQuantity(r.po_item_id, 'received', Number(v) || 0)}
                    style={{ width: 90 }}
                  />
                ),
              },
              {
                title: 'Accepted',
                key: 'accepted',
                width: 90,
                render: (_: unknown, r: GRNLineDTO) => (
                  <InputNumber
                    min={0}
                    value={lineQuantities[r.po_item_id]?.accepted ?? Number(r.accepted_quantity)}
                    onChange={(v) => setQuantity(r.po_item_id, 'accepted', Number(v) || 0)}
                    style={{ width: 70 }}
                  />
                ),
              },
              {
                title: 'Rejected',
                key: 'rejected',
                width: 80,
                render: (_: unknown, r: GRNLineDTO) => (
                  <InputNumber
                    min={0}
                    value={lineQuantities[r.po_item_id]?.rejected ?? 0}
                    onChange={(v) => setQuantity(r.po_item_id, 'rejected', Number(v) || 0)}
                    style={{ width: 70 }}
                  />
                ),
              },
              {
                title: 'Hold',
                key: 'hold',
                width: 70,
                render: (_: unknown, r: GRNLineDTO) => (
                  <InputNumber
                    min={0}
                    value={lineQuantities[r.po_item_id]?.hold ?? 0}
                    onChange={(v) => setQuantity(r.po_item_id, 'hold', Number(v) || 0)}
                    style={{ width: 60 }}
                  />
                ),
              },
              ...(items.some((l) => lineTheoreticalWeight(l, poMeta.get(l.po_item_id)) != null || l.actual_weight != null)
                ? [
                    {
                      title: 'Actual weight (kg)',
                      key: 'actual_weight',
                      width: 170,
                      render: (_: unknown, r: GRNLineDTO) => {
                        const meta = poMeta.get(r.po_item_id);
                        return (
                          <GrnActualWeightInput
                            expected={expectedReceiptWeight(
                              lineTheoreticalWeight(r, meta),
                              lineQuantities[r.po_item_id]?.received ?? Number(r.received_quantity),
                              r.ordered_quantity
                            )}
                            actual={actualWeightByLine[r.po_item_id]}
                            tolerancePlusPct={meta?.weight_tolerance_plus_pct}
                            toleranceMinusPct={meta?.weight_tolerance_minus_pct}
                            onChange={(v) => setActualWeightByLine((prev) => ({ ...prev, [r.po_item_id]: v }))}
                          />
                        );
                      },
                    },
                  ]
                : []),
            ]}
          />
        </Card>

        {items.some((line) => {
          const meta = poMeta.get(line.po_item_id);
          return grnLineNeedsHeat(meta, linkedItemFor(line)) || (heatLinesByPoItem[line.po_item_id]?.length ?? 0) > 0;
        }) && (
          <Card title="Heat lines & MTC" style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Critical items require heat number, dimension and quantity per heat (quantities must sum to accepted). Attach an MTC per heat using Upload MTC, or enter the document UUID or document number from Documents. Items flagged “MTC required on receipt” need a document on every heat line before you can save.
            </Text>
            {items.map((line) => {
              const meta = poMeta.get(line.po_item_id);
              const linked = linkedItemFor(line);
              const show =
                grnLineNeedsHeat(meta, linked) || (heatLinesByPoItem[line.po_item_id]?.length ?? 0) > 0;
              if (!show) return null;
              const rows = heatLinesByPoItem[line.po_item_id] || [
                { heat_number: '', dimension: '', quantity: Number(line.accepted_quantity) },
              ];
              return (
                <Card key={line.po_item_id} size="small" style={{ marginBottom: 12 }} type="inner" title={`${grnTraceItemCode(meta, linked) || line.item_code || ''} — ${line.item_description ?? ''}`}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {rows.map((row, idx) => (
                      <div key={row.id ?? idx} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 8, marginBottom: 8 }}>
                        <Space wrap style={{ marginBottom: 8 }}>
                          <Input
                            placeholder="Heat number"
                            style={{ width: 140 }}
                            value={row.heat_number}
                            onChange={(e) => updateHeatLine(line.po_item_id, idx, { heat_number: e.target.value })}
                          />
                          <Input
                            placeholder="Dimension"
                            style={{ width: 140 }}
                            value={row.dimension}
                            onChange={(e) => updateHeatLine(line.po_item_id, idx, { dimension: e.target.value })}
                          />
                          <InputNumber
                            min={0}
                            value={row.quantity}
                            onChange={(v) => updateHeatLine(line.po_item_id, idx, { quantity: Number(v) || 0 })}
                            style={{ width: 100 }}
                          />
                          <Input
                            placeholder="Document UUID or number (optional)"
                            style={{ width: 220 }}
                            value={row.mtc_document_id ?? ''}
                            onChange={(e) =>
                              updateHeatLine(line.po_item_id, idx, {
                                mtc_document_id: e.target.value.trim() || null,
                              })
                            }
                          />
                          {rows.length > 1 && (
                            <Button type="link" danger onClick={() => removeHeatLine(line.po_item_id, idx)}>
                              Remove
                            </Button>
                          )}
                        </Space>
                        {row.id ? (
                          <DocumentUpload
                            entityType="GrnItemHeatLine"
                            entityId={row.id}
                            buttonText="Upload MTC"
                            onSuccess={(doc) => {
                              queryClient.invalidateQueries({ queryKey: ['goods-receipt-note', id] });
                              if (doc?.id) {
                                updateHeatLine(line.po_item_id, idx, { mtc_document_id: doc.id });
                              }
                            }}
                          />
                        ) : (
                          <DocumentUpload
                            entityType="PurchaseOrderItem"
                            entityId={line.po_item_id}
                            buttonText="Upload MTC"
                            onSuccess={(doc) => {
                              if (doc?.id) updateHeatLine(line.po_item_id, idx, { mtc_document_id: doc.id });
                            }}
                          />
                        )}
                        {grnLineNeedsMtc(meta, linked) ? (
                          <Text type="secondary" style={{ marginLeft: 8 }}>MTC required for this item.</Text>
                        ) : null}
                      </div>
                    ))}
                    <Button type="dashed" size="small" onClick={() => addHeatLine(line.po_item_id)}>
                      Add heat line
                    </Button>
                  </Space>
                </Card>
              );
            })}
          </Card>
        )}

        <Space>
          <Button type="primary" onClick={handleSubmit} loading={submitLoading}>
            Save changes
          </Button>
          <Button onClick={() => navigate(`/scm/procurement/grn/${id}`)}>Cancel</Button>
        </Space>
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_GOODS_RECEIPT_NOTE}
          entityId={id}
          queryKeyPrefix={['documents', 'goods-receipt-note']}
        />
      </Form>
    </div>
  );
};

export default EditGRNPage;
